import styled from 'styled-components'

// ─── Section wrapper ─────────────────────────────────────────────────────────

export const Section = styled.section`
  padding: 2.5rem 0 4rem;
  background: #fff;
  /* Prevent the wide carousel track from causing page-level horizontal scroll */
  overflow: hidden;
`


export const Heading = styled.h2`
  font-size: 2.3rem;
  font-weight: 700;
  margin: 1rem 0 1.5rem 2rem;
  color: #111;
  font-family: inherit;
`

// ─── Custom CSS-transform infinite carousel ──────────────────────────────────

/* position:relative + explicit height so the absolute Track is fully contained.
   Absolutely-positioned children NEVER contribute to page scroll-width. */
export const TrackViewport = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  /* clamp(min, preferred, max): scales smoothly with viewport width.
     ~42vw gives: 600px @ 1440px wide, ~370px @ 900px, clamped to 320px on mobile */
  height: clamp(350px, 35vw, 600px);
`



/* Track is taken OUT of the normal flow via position:absolute.
   This is the key fix: absolutely-positioned elements have zero layout width
   contribution, so this wide track can never cause a page horizontal scrollbar. */
export const Track = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-wrap: nowrap;
  width: calc(var(--n, 13) * 100% / 4.15);
  will-change: transform;
`

/* Each slide = 1/N of Track.width = viewport/4.15 (exactly one card wide) */
export const SlideItem = styled.div`
  flex: 0 0 calc(100% / var(--n, 13));
  height: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* Vertical padding so card box-shadow is not clipped by TrackViewport */
  padding: 0.75rem 0.5rem 1rem;

  &:first-child { padding-left: 2rem; }
`



// ─── Navigation (bottom-right) ────────────────────────────────────────────────

export const NavRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-right: 2rem;
`

export const NavButton = styled.button`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 1px solid #f5f5f5;
  background: #f2f2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #ccc;
  }

  &:active {
    background: #e0e0e0;
  }
`

// ─── Card ─────────────────────────────────────────────────────────────────────

export const Card = styled.div`
  background: #f2f2f2;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  user-select: none;
  overflow: hidden;
  padding: 0.75rem 0.75rem 0;
  cursor: pointer;

  /* ── Hover effects: image scale + overlay fade + button reveal ── */
  &:hover img {
    transform: scale(1.1);
  }

  &:hover [data-overlay] {
    background: rgba(0, 0, 0, 0.45);
  }

  &:hover [data-cta-btn] {
    opacity: 1;
    pointer-events: auto;
    transform: translate(-50%, -50%);
  }
`


export const CardImageWrapper = styled.div`
  width: 100%;
  min-height: 0;
  background: rgb(213, 217, 223);
  border-radius: 12px;
  aspect-ratio: 1/1;
  overflow: hidden;
  flex-shrink: 0;
  /* Needed for absolute-positioned overlay and button */
  position: relative;
`

export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform 0.8s ease;
`

/* Dark overlay — fades in on card hover */
export const CardImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  transition: background 0.3s ease;
  border-radius: 6px;
  pointer-events: none;
`

/* CTA button — centred on image, slides up + fades in on hover */
export const CardHoverButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 12px));
  opacity: 0;
  transition: opacity 0.3s ease, transform 0.3s ease;
  pointer-events: none;

  background: #0082FF;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: none;
  border-radius: 999px;
  padding: 0.85rem 1.7rem;
  cursor: pointer;
  white-space: nowrap;
`


export const CardBody = styled.div`
  /* Fixed height = consistent card body across all cards, regardless of title length.
     This prevents cards with longer titles from pushing the image up. */
  height: 8rem;
  flex-shrink: 0;
  padding: 0.75rem;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.5rem;
`


export const CardTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111;
  flex: 1;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

/* All badges share the same neutral color regardless of item type,
   matching the reference design where badge is a uniform neutral pill. */
export const CardTypeBadge = styled.span<{ itemType: string }>`
  flex-shrink: 0;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 8px 12px;
  border-radius: 40px;
  align-self: flex-end;
  background: #e5e7eb;
  color: #555;
`

